import type { SelectedDateRange } from 'common/components/DateRangeSelect';

import { z } from 'zod';

import { StartingPointId, VehicleType } from 'common/api/api-types';
import { formatLocalISO } from 'common/utils/timeUtils';
import { Timeframe as FinancialTimeframe } from 'features/earnings/utils';
import { Timeframe as OverviewTimeframe } from 'features/overview/constants';
import { Timeframe } from 'features/performance/courierList/timeframePickerTypes';

export const vehicleTypeSet = z.codec(z.array(VehicleType), z.custom<Set<VehicleType>>(), {
  decode: arr => new Set(arr),
  encode: set => [...set],
});

export const stringSet = z.codec(z.array(z.string()), z.custom<Set<string>>(), {
  decode: arr => new Set(arr),
  encode: set => [...set],
});

export const yesNo = z.codec(z.enum(['yes', 'no']), z.boolean(), {
  decode: v => v === 'yes',
  encode: b => (b ? 'yes' : 'no'),
});

const localDateTime = z.iso.datetime({ local: true });

export const selectedPerformanceDateRange = z.codec(
  z.union([
    z.tuple([z.literal('custom'), localDateTime, localDateTime]),
    z.tuple([Timeframe.exclude(['custom'])]),
  ]),
  z.custom<SelectedDateRange>(),
  {
    decode: (arr): SelectedDateRange => {
      if (arr[0] === 'custom') {
        return { value: 'custom', from: new Date(arr[1]), to: new Date(arr[2]) };
      }
      return { value: arr[0], from: undefined };
    },
    encode: (range, ctx) => {
      if (range.value === 'custom') {
        if (!range.from) {
          ctx.issues.push({ code: 'custom', input: range, message: '`from` parameter is missing' });
        }
        if (!range.to) {
          ctx.issues.push({ code: 'custom', input: range, message: '`to` parameter is missing' });
        }
        if (!range.from || !range.to) {
          return z.NEVER;
        }

        const result: ['custom', string, string] = [
          'custom',
          formatLocalISO(range.from),
          formatLocalISO(range.to),
        ];

        return result;
      }

      const result: [Exclude<Timeframe, 'custom'>] = [range.value];
      return result;
    },
  },
);

export const selectedFinancialDateRange = z.codec(
  z.union([
    z.tuple([z.literal('custom'), localDateTime, localDateTime]),
    z.tuple([FinancialTimeframe.exclude(['custom'])]),
  ]),
  z.custom<SelectedDateRange>(),
  {
    decode: (arr): SelectedDateRange => {
      if (arr[0] === 'custom') {
        return { value: 'custom', from: new Date(arr[1]), to: new Date(arr[2]) };
      }
      return { value: arr[0], from: undefined };
    },
    encode: (range, ctx) => {
      if (range.value === 'custom') {
        if (!range.from) {
          ctx.issues.push({ code: 'custom', input: range, message: '`from` parameter is missing' });
        }
        if (!range.to) {
          ctx.issues.push({ code: 'custom', input: range, message: '`to` parameter is missing' });
        }
        if (!range.from || !range.to) {
          return z.NEVER;
        }

        const result: ['custom', string, string] = [
          'custom',
          formatLocalISO(range.from),
          formatLocalISO(range.to),
        ];

        return result;
      }

      const result: [Exclude<FinancialTimeframe, 'custom'>] = [range.value];
      return result;
    },
  },
);

export const startingPointId = z.codec(
  z.string().check(z.refine(s => Number.isFinite(Number(s)))),
  z.custom<StartingPointId>(),
  {
    decode: str => StartingPointId.parse(Number(str)),
    encode: id => String(id),
  },
);

const isoDate = z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/));

export const scheduleDateRange = z.codec(
  z.tuple([isoDate, isoDate]),
  z.custom<[from: string, to: string]>(),
  {
    decode: ([from, to]): [string, string] => [from, to],
    encode: ([from, to]): [string, string] => [from, to],
  },
);

export const overviewTimeframe = z.codec(OverviewTimeframe, OverviewTimeframe, {
  decode: str => str,
  encode: tf => tf,
});
